import {DomainObject} from 'shared/store';
import {observable, computed, action, makeObservable} from 'mobx';
import moment from 'moment';
import _ from 'lodash';
import {t} from 'shared/core';
import {atTimeOfDay, calendarDay} from 'shared/tools';
import {Employee} from 'main/stores/employees';
import TimeTrackingDimensionValue from './TimeTrackingDimensionValue';
import ScheduleRule from './ScheduleRule';

class Shift extends DomainObject {
  startTime = null;
  endTime = null;
  date = null;
  startTimeString = '';
  endTimeString = '';
  unpaidBreakMinutes = 0;
  note = '';
  publishedAt = null;
  employee = null;
  schedule = null;
  dimensionValues = [];
  scheduleRule = null;

  _relations = {
    employee: Employee,
    dimensionValues: [TimeTrackingDimensionValue],
    scheduleRule: ScheduleRule
  };

  constructor(other) {
    super();

    makeObservable(this, {
      startTime: observable,
      endTime: observable,
      date: observable,
      startTimeString: observable,
      endTimeString: observable,
      unpaidBreakMinutes: observable,
      note: observable,
      publishedAt: observable,
      employee: observable,
      schedule: observable,
      dimensionValues: observable,
      scheduleRule: observable,
      startTimeFromString: computed,
      endTimeFromString: computed,
      spansMidnight: computed,
      employeeId: computed,
      dimensionValueIds: computed,
      published: computed,
      paidHours: computed,
      timeRange: computed,
      setDimensionValue: action
    });

    this.merge(other);

    if (this.startTime && !this.startTimeString) {
      const timeFormat = t('components.dates.TIMESTAMP_FORMAT');

      this.date = calendarDay(this.startTime);
      this.startTimeString = moment.utc(this.startTime).format(timeFormat);
      this.endTimeString = moment.utc(this.endTime).format(timeFormat);
    }
  }

  get startTimeFromString() {
    const startTime = atTimeOfDay(this.date, this.startTimeString);

    return startTime && moment(startTime);
  }

  get endTimeFromString() {
    const endTime = atTimeOfDay(this.date, this.endTimeString);
    if (!endTime) return null;

    return this.spansMidnight ? moment(endTime).add(1, 'day') : moment(endTime);
  }

  get spansMidnight() {
    const startTime = atTimeOfDay(this.date, this.startTimeString);
    const endTime = atTimeOfDay(this.date, this.endTimeString);

    return !!startTime && !!endTime && endTime < startTime;
  }

  get employeeId() {
    return _.get(this.employee, 'id', null);
  }

  get dimensionValueIds() {
    return _.map(this.dimensionValues, 'id');
  }

  get published() {
    return !!this.publishedAt;
  }

  get paidHours() {
    if (!this.startTimeFromString || !this.endTimeFromString) return 0;

    const minutes = this.endTimeFromString.clone().utc(true).diff(this.startTimeFromString.clone().utc(true), 'minutes') - Number(this.unpaidBreakMinutes || 0);

    return Math.max(minutes, 0) / 60;
  }

  get timeRange() {
    if (!this.startTimeString || !this.endTimeString) return null;

    return t('components.dates.DATE_SPAN', {
      startDate: this.startTimeString,
      endDate: this.endTimeString
    });
  }

  toJS() {
    return {
      ...super.toJS(),
      startTime: this.startTimeFromString && this.startTimeFromString.clone().utc(true).format(),
      endTime: this.endTimeFromString && this.endTimeFromString.clone().utc(true).format(),
      note: this.note || null,
      scheduleRule: this.scheduleRule && this.scheduleRule.toJS()
    };
  }

  dimensionValueIdFor(dimension) {
    return _.find(dimension.values, value => _.includes(this.dimensionValueIds, value.id))?.id;
  }

  setDimensionValue(dimension, value) {
    const otherDimensionValues = _.reject(this.dimensionValues, dimensionValue => _.some(dimension.values, {id: dimensionValue.id}));

    this.dimensionValues = value ? [...otherDimensionValues, value] : otherDimensionValues;
  }
}

export default Shift;
